import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../../service/Api";
import { Loader2, Printer } from "lucide-react";
import CollateralPreview from "../../components/collateral/CollateralPreview";
import CollateralChatbot from "../../components/collateral/CollateralChatbot";
import { loadFontsFromData } from "../../utils/fontLoader";
import { useImageProxy } from "../../hooks/useImageProxy";
import { extractImageUrls, mapBlobUrlsToData, createUrlMap } from "../../utils/imageDataConverter";


import { useReactToPrint } from "react-to-print";
import { useRef } from "react";



export default function TailspinAgentic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  


  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  // Version management
  const [allVersions, setAllVersions] = useState([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);
  
  // Chatbot states
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Image conversion - extract image URLs from current data (memoized to avoid re-renders)
  const imageUrls = useMemo(() => {
    return data ? extractImageUrls(data) : [];
  }, [data]);
  
  const { blobUrls: convertedBlobUrls, loading: imagesLoading } = useImageProxy(
    imageUrls.length > 0 ? imageUrls : null
  );
  
  // Converted data with blob URLs
  const [convertedData, setConvertedData] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    async function fetchData() {
      try {
        const response = await apiService.getCompaniesCollateralBySlug(slug);
        
        console.log("Response:", response);
        
        // Handle array response (multiple versions)
        const versions = Array.isArray(response?.data?.data) 
          ? response.data.data 
          : [response?.data?.data].filter(Boolean);
        
        if (versions.length === 0) {
          setError("No collateral data found.");
          return;
        }
        
        setAllVersions(versions);
        setCurrentVersionIndex(0); // Always show the first (newest) version initially
        
        // Set initial data from index 0
        const initialVersion = versions[0];
        setData(initialVersion?.data);
        setTemplate(initialVersion?.template);
        
        // Load fonts from data
        await loadFontsFromData(initialVersion?.data);
        
        console.log("Full versions:", versions);
        console.log("Showing version:", 0);
        console.log("🖼️ Extracted image URLs:", extractImageUrls(initialVersion?.data));
      } catch (err) {
        console.error(err);
        setError("Failed to fetch template or data.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [slug]);

  const handleVersionSwitch = async (index) => {
    if (index >= 0 && index < allVersions.length) {
      setCurrentVersionIndex(index);
      const version = allVersions[index];
      setData(version?.data);
      setTemplate(version?.template);
      
      // Load fonts for the switched version
      await loadFontsFromData(version?.data);
      
      console.log("🖼️ Extracted image URLs for version:", extractImageUrls(version?.data));
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isSending) return;

    const messageContent = inputValue.trim();

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: messageContent,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsSending(true);

    try {
      // Send current version index to backend
      const response = await apiService.editCompnayCollateral(slug, messageContent, currentVersionIndex);
      
      if (response?.data?.success) {
        // Refresh all versions after edit
        const refreshResponse = await apiService.getCompaniesCollateralBySlug(slug);
        const versions = Array.isArray(refreshResponse?.data?.data) 
          ? refreshResponse.data.data 
          : [refreshResponse?.data?.data].filter(Boolean);
        
        setAllVersions(versions);
        setCurrentVersionIndex(0); // New version is always at index 0
        
        // Update preview with new version
        const newVersion = versions[0];
        setData(newVersion?.data);
        setTemplate(newVersion?.template);
        
        // Load fonts for the new version
        await loadFontsFromData(newVersion?.data);
        
        console.log("🖼️ Extracted image URLs for new version:", extractImageUrls(newVersion?.data));

        const botMessage = {
          id: Date.now() + 1,
          type: "bot",
          content: "✓ Collateral updated successfully! The preview has been refreshed with your changes.",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          data: newVersion?.data,
          versions: versions
        };

        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error("Failed to update collateral");
      }
    } catch (err) {
      console.error("Error editing collateral:", err);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: "❌ Sorry, I couldn't process your request. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  const handleInputChange = (e) => setInputValue(e.target.value);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleErrorReport = (errorMessage) => {
    // Set the error message in the input field
    setInputValue(errorMessage);
    // Focus the input field if possible (using a small timeout to ensure DOM is ready)
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder="Describe your changes..."]');
      if (textarea) {
        textarea.focus();
        // Scroll to the bottom of the textarea to show the content
        textarea.scrollTop = textarea.scrollHeight;
      }
    }, 100);
  };

  const handleQuickEdit = async (message) =>   {  // Set the message in the input field and automatically send it
    if (!message.trim() || isSending) return;

    const messageContent = message.trim();

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: messageContent,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setIsSending(true);

    try {
      // Send current version index to backend
      const response = await apiService.editCompnayCollateral(slug, messageContent, currentVersionIndex);
      
      if (response?.data?.success) {
        // Refresh all versions after edit
        const refreshResponse = await apiService.getCompaniesCollateralBySlug(slug);
        const versions = Array.isArray(refreshResponse?.data?.data) 
          ? refreshResponse.data.data 
          : [refreshResponse?.data?.data].filter(Boolean);
        
        setAllVersions(versions);
        setCurrentVersionIndex(0); // New version is always at index 0
        
        // Update preview with new version
        const newVersion = versions[0];
        setData(newVersion?.data);
        setTemplate(newVersion?.template);
        
        // Load fonts for the new version
        await loadFontsFromData(newVersion?.data);
        
        console.log("🖼️ Extracted image URLs for new version:", extractImageUrls(newVersion?.data));

        const botMessage = {
          id: Date.now() + 1,
          type: "bot",
          content: "✓ Collateral updated successfully! The preview has been refreshed with your changes.",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          data: newVersion?.data,
          versions: versions
        };

        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error("Failed to update collateral");
      }
    } catch (err) {
      console.error("Error editing collateral:", err);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: "❌ Sorry, I couldn't process your request. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  // Convert data when blob URLs are ready
  useEffect(() => {
    if (!data) {
      setConvertedData(null);
      return;
    }

    // If there are no images to convert, use original data
    if (imageUrls.length === 0) {
      setConvertedData(data);
      return;
    }

    // Wait for blob URLs to be ready
    if (!convertedBlobUrls || !Array.isArray(convertedBlobUrls)) {
      return;
    }

    // Create URL mapping
    const urlMap = createUrlMap(imageUrls, convertedBlobUrls);
    
    // Map blob URLs back to data structure
    const converted = mapBlobUrlsToData(data, urlMap);
    setConvertedData(converted);
    
    console.log("✅ Converted data with blob URLs:", {
      originalUrls: imageUrls.length,
      convertedUrls: convertedBlobUrls.filter(Boolean).length
    });
  }, [data, convertedBlobUrls, imageUrls]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#FF6B5A' }} />
          <p style={{ color: '#0A0A10' }}>Loading collateral...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center p-6 rounded-lg" style={{ color: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)' }}>
          <p className="font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  if (!template || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div style={{ color: '#0A0A10' }}>No template or data received.</div>
      </div>
    );
  }


  return (
    <div className="flex h-screen" style={{ backgroundColor: '#F8F7F4' }}>
      {/* Left Side - Chatbot */}
      <CollateralChatbot
        messages={messages}
        inputValue={inputValue}
        isSending={isSending}
        onInputChange={handleInputChange}
        onSendMessage={handleSendMessage}
        onKeyPress={handleKeyPress}
        allVersions={allVersions}
        currentVersionIndex={currentVersionIndex}
        onVersionSwitch={handleVersionSwitch}
      />

      {/* Right Side - Collateral Preview */}
      <div className="flex-[3] overflow-y-auto border-l" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
        {/* Header Toolbar */}
        <div className="sticky top-0 z-10 border-b px-6 py-4 flex items-center justify-between no-print" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
          <h2 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>Preview</h2>
          <button
            onClick={reactToPrintFn}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
            style={{ backgroundColor: '#FF6B5A' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
            title="Print collateral"
          >
            <Printer className="w-4 h-4" />
            <span>Download as PDF</span>
          </button>
        </div>
        
        {/* Preview Content */}
        <div ref={contentRef} className="p-6">
          <CollateralPreview 
            template={template} 
            data={convertedData || data} 
            withContainer={false}
            onErrorReport={handleErrorReport}
            onQuickEdit={handleQuickEdit}
          />
        </div>
      </div>
    </div>
  );
}